/**
 * Use a hexColor value and a number and it will return a css rgba(r,g,b,a).
 * @example
 *
 * getRgbaColor('#25477B', 50) will give `rgba(37, 71, 123, 0.5)`
 */
export function getRgbaColor(hexColor, opacity = 1) {
    return `rgba(${getRgbColor(hexColor)},${opacity})`;
}
/**
 * Use a hexColor value a css rgb(r,g,b).
 * @example
 *
 * getRgbColor('#25477B') will give `rgb(37, 71, 123)`
 */
export function getRgbColor(hexColor) {
    hexColor = hexColor.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (_, r, g, b) => r + r + g + g + b + b);
    const parseHex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
    return parseHex ? `${parseInt(parseHex[1], 16)},${parseInt(parseHex[2], 16)},${parseInt(parseHex[3], 16)}` : '';
}
/**
 * Use a hexColor value and it will return a css hsl(h,s,l).
 * @example
 *
 * getHslColor('#25477B') will give `hsl(216, 54%, 31%)`
 */
export function getHslColor(hexColor) {
    const rgbColor = getRgbColor(hexColor)
        .split(',')
        .map((color) => parseInt(color));
    const red = (rgbColor[0] /= 255);
    const green = (rgbColor[1] /= 255);
    const blue = (rgbColor[2] /= 255);
    const colorMin = Math.min(red, green, blue);
    const colorMax = Math.max(red, green, blue);
    const colorDelta = colorMax - colorMin;
    let hue, saturation, lightness = 0;
    if (colorDelta === 0) {
        hue = 0;
    }
    else if (colorMax === red) {
        hue = ((green - blue) / colorDelta) % 6;
    }
    else if (colorMax === green) {
        hue = (blue - red) / colorDelta + 2;
    }
    else {
        hue = (red - green) / colorDelta + 4;
    }
    hue = Math.round(hue * 60);
    if (hue < 0) {
        hue += 360;
    }
    lightness = (colorMax + colorMin) / 2;
    saturation = colorDelta == 0 ? 0 : colorDelta / (1 - Math.abs(2 * lightness - 1));
    saturation = +(saturation * 100).toFixed(1);
    lightness = +(lightness * 100).toFixed(1);
    return `${Math.round(hue)},${Math.round(saturation)}%,${Math.round(lightness)}%`;
}
/**
 * Use a hexColor value and it will return an accessible color value to use as a border.
 * @example
 *
 * getHslColor('#25477B') will give `hsl(216, 54%, 17%)`
 */
export function getAccessibleShade(hexColor) {
    const hslColor = getHslColor(hexColor)
        .split(',')
        .map((color) => parseInt(color));
    let multiplier = 0.75;
    if (hslColor[0] >= 40 && hslColor[0] <= 90) {
        multiplier = 0.55;
    }
    return `${hslColor[0]},${hslColor[1]}%,${hslColor[2] * multiplier}%`;
}
